// hexOverlay.ts - the H3 claim grid, on whatever body is focused.
//
// H3 subdivides an icosahedron inscribed in the UNIT sphere, so a cell is a set
// of directions and no radius appears in its construction. A body's radius only
// scales the cell - area by (R / R_earth)^2 - which is why canon sets one
// resolution per body and nothing else about the grid is per body.
//
// The registry resolution is the CLAIM cell: canon's datum registry picks, on
// every body, the resolution whose mean cell comes nearest 10 km flat to flat
// (`../4xciv/phase-h/03-land/02-datum-registry.md` II.4). So this overlay
// aggregates UPWARD and never draws finer than the registry says: at altitude a
// base cell is too small to read and its parent is drawn instead, and at every
// altitude where a base cell IS readable the drawn resolution is the registry
// number itself. That last property is what makes the grid parametric in the
// registry rather than in the pixel rule - step the row's resolution and the
// drawn cell count moves with it.
//
// No hex area is stored anywhere. Area, edge length and pixels per cell all
// fall out of the resolution and `radius_m` at the point of use.
//
// Every vertex is draped onto the loaded ground (`HexOverlayView.heightAt`)
// rather than floated on the datum, and a resolution change cross-fades two
// line sets rather than popping (`resolutionFade`). A pentagon in the cap
// draws with its five edges same as any other cell; nothing about it is
// special-cased, which is what makes `pentagons` a rendering decision instead
// of a crash.

import * as THREE from 'three';
import { wrap180HalfOpen } from '../math/angles.ts';

type H3 = typeof import('h3-js');

/** IUGG mean Earth radius, the sphere h3-js's published cell areas are on. */
export const R_EARTH_M = 6371008.8;

/** Area of a regular hexagon of across-flats w is 0.866 w^2. */
const HEX_AREA_OVER_W2 = Math.sqrt(3) / 2;

/**
 * The readable floor, pixels across flats.
 *
 * Below this a boundary line is noise; above about 100 the next finer rung
 * should have taken over, and sqrt(7) between rungs puts the window at 24 to
 * 63.5 (`docs/roadmap/06-hex-grid-and-claims.md`, "Rendering hexes by altitude").
 */
export const READABLE_PX = 24;

/** One H3 step changes area by 7, so across-flats by sqrt(7). */
const STEP = Math.sqrt(7);

/**
 * How far a body's surface may depart from the datum sphere and still carry a grid.
 *
 * A fraction of the datum radius, read off the row's own ellipsoid semi-axes.
 * H3 is a partition of a SPHERE and this file drapes its boundaries on one; on
 * a body whose drawn mesh is a shape model a tenth of a radius off that sphere,
 * a draped line crosses in and out of the mesh along its own length and the
 * grid tears into fragments no depth bias can fix. Such a body draws no grid at
 * all, the way one with no `default_h3_resolution` does. Measured against the
 * registry (`public/art/atlas/bodies.json`): Vesta 0.128, Pallas 0.125, Psyche
 * 0.234 and the moons Phobos 0.179, Deimos 0.258 and Bennu 0.057 sit above or
 * near it; Ceres 0.051 and every round body are far under.
 */
export const SHAPE_DEPARTURE_LIMIT = 0.1;

/** The chord sag a grid line may carry, in pixels of the frame it is drawn in. */
const SAG_PX = 0.25;

/**
 * Ceiling on the depth bias, as a fraction of the datum radius.
 *
 * The bias has to cover the sag `SAG_PX` allows or the chord dips under the
 * ground between two draped vertices, and at ORBIT a quarter pixel is
 * kilometres. This bounds it well under the datum float `--inject float` puts
 * the grid on (`LIFT`, 0.0009 of the radius), so the drape reading still has a
 * control that fails; the subdivision below takes up whatever slack the
 * ceiling leaves.
 */
const BIAS_MAX_RADII = 2e-4;

/**
 * Refuse to bisect an edge past this depth: 2^10 segments is already 1024.
 *
 * 8 was enough while the drape read the height field's own smooth bilinear
 * surface. It reads the lattice the tiles are MESHED on instead, which is
 * piecewise linear and creases at every quad boundary, and 256 segments left
 * 174 chords at SURFACE still dipping under a crease.
 */
const ARC_MAX_DEPTH = 10;

/**
 * The grid's own night-side floor, a fraction of full sun-facing brightness.
 *
 * The datum-normal Lambert term this multiplies goes to 0 past the
 * terminator, which would make the grid invisible rather than dim; this
 * keeps it faintly legible in the terrain's own ambient without ever putting
 * it above the lit side's own 1.0.
 */
const NIGHT_FLOOR = 0.02;

/** How far off the datum the MARKER floats, as a fraction of the radius. */
const LIFT = 1.0009;

/**
 * Clearance a grid line keeps above the ground it is draped onto, metres.
 *
 * Round 1's flat `HEX_LIFT` put the whole grid a thousandth of a radius above
 * the datum - kilometres, on Mars - which is fine floating over nothing but
 * wrong sitting on real relief. A vertex with a real height source needs only
 * enough clearance to keep the line off the mesh it is drawn on top of.
 */
export const DEPTH_BIAS_M = 0.5;

/**
 * Width of the cross-fade between two adjacent resolutions, in decades
 * (log10) of altitude.
 *
 * A full aggregation step is `log10(sqrt(7))` = 0.42 decades. 0.4 keeps the
 * fade windows either side of a step from reaching past its midpoint, so a
 * camera is never blending three resolutions at once - just the two either
 * side of whichever boundary it is nearest, which is "about half a decade".
 */
export const FADE_DECADES = 0.4;

/** Refuse to build a set this big: something upstream is wrong. */
const CELL_CEILING = 20000;

/** Mean across-flats of a cell at `res` on a body of this radius, metres. */
export function cellAcrossFlatsM(h3: H3, res: number, radiusM: number): number {
  const areaKm2 = h3.getHexagonAreaAvg(res, 'km2') * (radiusM / R_EARTH_M) ** 2;
  return Math.sqrt(areaKm2 / HEX_AREA_OVER_W2) * 1000;
}

/** How many pixels across flats a cell of this width reads at this distance. */
export function pixelsAcross(
  widthM: number, distanceM: number, viewportH: number, fovDeg: number,
): number {
  const k = viewportH / (2 * Math.tan(THREE.MathUtils.degToRad(fovDeg) / 2));
  return (k * widthM) / Math.max(1, distanceM);
}

/** The distance at which a base-resolution cell reads exactly `READABLE_PX`. */
function baseDistanceM(
  h3: H3, base: number, radiusM: number, viewportH: number, fovDeg: number,
): number {
  const wBase = cellAcrossFlatsM(h3, base, radiusM);
  const k = viewportH / (2 * Math.tan(THREE.MathUtils.degToRad(fovDeg) / 2));
  return (k * wBase) / READABLE_PX;
}

/**
 * How many aggregation steps above the base this altitude stands, fractional.
 *
 * The rule itself, in one place: `d_base` is the distance at which a base cell
 * reads exactly `READABLE_PX`, and one parent step is sqrt(7) of distance past
 * it. `drawnResolution` rounds it up; `resolutionFade` needs where inside a
 * step it fell, which is the only reason it is fractional here.
 */
function stepsAbove(
  h3: H3, base: number, radiusM: number, altitudeM: number,
  viewportH: number, fovDeg: number,
): number {
  const dBase = baseDistanceM(h3, base, radiusM, viewportH, fovDeg);
  return Math.log(Math.max(1, altitudeM) / dBase) / Math.log(STEP);
}

/** The resolution drawn at this altitude: the registry base, or a parent of it. */
export function drawnResolution(
  h3: H3, base: number, radiusM: number, altitudeM: number,
  viewportH: number, fovDeg: number,
): number {
  const j = Math.max(0, Math.ceil(stepsAbove(h3, base, radiusM, altitudeM, viewportH, fovDeg)));
  return THREE.MathUtils.clamp(base - j, 0, base);
}

export interface ResolutionFade {
  /** The resolution `drawnResolution` itself would return at this altitude. */
  resA: number;
  /** The neighbour fading in, or `resA` again when no boundary is close. */
  resB: number;
  weightA: number;
  weightB: number;
}

/**
 * The one or two resolutions in view at this altitude, and how much of each.
 *
 * `drawnResolution` is a step function: a camera crossing one of its
 * boundaries swaps the whole grid in a single frame. This blends the two
 * resolutions either side of a boundary across `FADE_DECADES` of altitude, so
 * `resA` always agrees with `drawnResolution` and `resB` is whichever
 * neighbour - finer below the bracket, coarser above it - is close enough to
 * be fading in. Away from a boundary `weightB` is 0 and only `resA` draws,
 * which is round 1's behaviour exactly.
 */
export function resolutionFade(
  h3: H3, base: number, radiusM: number, altitudeM: number,
  viewportH: number, fovDeg: number,
): ResolutionFade {
  const s = stepsAbove(h3, base, radiusM, altitudeM, viewportH, fovDeg);
  const j = Math.max(0, Math.ceil(s));
  const resA = THREE.MathUtils.clamp(base - j, 0, base);
  const log10Step = Math.log10(STEP);
  const halfWin = FADE_DECADES / 2;
  // Distance in decades from s to each edge of the bracket s currently sits
  // in: the lower edge is where a finer resolution takes over, the upper
  // where a coarser one does.
  const distLower = (s - (j - 1)) * log10Step;
  const distUpper = (j - s) * log10Step;
  const edgeWeight = (dist: number) => THREE.MathUtils.clamp(1 - dist / halfWin, 0, 1);
  const wFiner = resA < base ? edgeWeight(distLower) : 0;
  const wCoarser = resA > 0 ? edgeWeight(distUpper) : 0;
  if (wFiner >= wCoarser && wFiner > 0) {
    return { resA, resB: resA + 1, weightA: 1 - wFiner, weightB: wFiner };
  }
  if (wCoarser > 0) {
    return { resA, resB: resA - 1, weightA: 1 - wCoarser, weightB: wCoarser };
  }
  return { resA, resB: resA, weightA: 1, weightB: 0 };
}

/**
 * Half-angle of the ground the camera can see, radians, about the SUB-CAMERA point.
 *
 * The horizon is the whole bound: a surface point is visible from radius rc
 * while its great-circle angle from the nadir stays under acos(R/rc), the same
 * form `tileTree.ts` culls with, and nothing outside it can be in frame however
 * the frame is aimed. There is deliberately no frustum term. Every band above
 * SURFACE frames its focus at a shallow pitch, which stands the eye far to the
 * SIDE of the ground it looks at - at ORBIT on Ceres, 67 degrees of arc from
 * it - so a footprint derived from a nadir view and the focus-relative slant
 * distance is neither centred where the disc is nor as wide as the frame
 * reaches, and it left three quarters of the visible ground with no grid on it.
 *
 * One cell of slack past the horizon, because a cell joins the fill on its
 * CENTRE being inside: without it the last row at the limb loses the edges that
 * reach into view, which is the jagged hex-shaped edge the cap used to end on.
 */
export function capHalfAngleRad(
  radiusM: number, camRadiusM: number, cellAcrossFlatsM: number, groundRadiusM: number,
): number {
  const r = Math.max(1, radiusM);
  // The horizon is set by the eye's height over the ground UNDER it, not over
  // the datum sphere. At Jezero the ground is 2.5 km inside that sphere and
  // the eye at CLAIM is inside it too, which collapsed the cap to the one-ring
  // floor over a basin. The datum is the ceiling on this because ground beyond
  // the eye can stand above it.
  const rg = Math.max(1, Math.min(r, groundRadiusM));
  const rc = Math.max(rg + 1, camRadiusM);
  const horizon = Math.acos(THREE.MathUtils.clamp(rg / rc, -1, 1));
  return Math.min(Math.PI, horizon + Math.max(0, cellAcrossFlatsM) / r);
}

/**
 * The radius of the sphere the eye's own horizon stands on, metres.
 *
 * The ground under the eye where the height source has it, the datum sphere
 * where it does not or where that ground is above the datum.
 */
export function horizonRadiusM(view: HexOverlayView): number {
  const h = view.heightAt(view.camLatDeg, view.camLonDeg);
  return view.radiusM + Math.min(0, h ?? 0);
}

/** The three grounds the grid can be draped on, metres above the datum or null. */
export interface HexGrounds {
  /** The plot's own height field, null outside its footprint. */
  field(latDeg: number, lonDeg: number): number | null;
  /** The tile pyramid's drawn leaves. */
  leaves(latDeg: number, lonDeg: number): number | null;
  globe(latDeg: number, lonDeg: number): number | null;
}

/** What the frame draws: the band, whether a plot is up, whether the far field splits the frame. */
export interface HexGroundPass {
  readonly surface: boolean;
  readonly plot: boolean;
  readonly farField: boolean;
}

/**
 * The ground the grid is drawn against, which is the ground it drapes on.
 *
 * The grid draws with the leaves. With the far field on, `drawFrame` in
 * `src/atlas.ts` clears depth and draws the plot mesh alone after them, so the
 * plot's field is the grid's ground only when the frame is one pass.
 */
export function hexGroundHeightAt(
  latDeg: number, lonDeg: number, pass: HexGroundPass, ground: HexGrounds,
): number | null {
  if (pass.surface && pass.plot && !pass.farField) {
    const h = ground.field(latDeg, lonDeg);
    if (h !== null && Number.isFinite(h)) return h;
  }
  return ground.leaves(latDeg, lonDeg) ?? ground.globe(latDeg, lonDeg);
}

/**
 * The cap this file derived before the reach reading was built, radians.
 *
 * `--inject hex-legacy`'s half: centred on the FOCUS rather than the eye, with
 * the horizon taken off the focus-relative slant distance and a nadir-view
 * frustum footprint widened 15 percent. Kept because the reach reading has to
 * have something it fails on, and this is what it read as three quarters of
 * Ceres having no grid on it.
 */
export function legacyCapHalfAngleRad(
  radiusM: number, altitudeM: number, fovDeg: number, aspect: number,
): number {
  const rc = radiusM + Math.max(1, altitudeM);
  const horizon = Math.acos(THREE.MathUtils.clamp(radiusM / rc, -1, 1));
  const halfV = Math.tan(THREE.MathUtils.degToRad(fovDeg) / 2);
  const halfDiag = Math.hypot(halfV, halfV * Math.max(1, aspect));
  const frustum = Math.atan((altitudeM * halfDiag * 1.15) / radiusM);
  return Math.min(horizon, frustum);
}

/**
 * How far a body's own surface departs from the datum sphere, as a fraction of it.
 *
 * The widest of the row's ellipsoid semi-axes against the datum radius, which
 * is what `SHAPE_DEPARTURE_LIMIT` is read against. A row with no ellipsoid is
 * a sphere as far as anything here can tell, and reads 0.
 */
export function shapeDepartureFrac(
  radiusM: number, axesM: readonly number[] | null,
): number {
  const r = Math.max(1, radiusM);
  if (!axesM || axesM.length === 0) return 0;
  let worst = 0;
  for (const a of axesM) {
    if (!Number.isFinite(a) || a <= 0) continue;
    worst = Math.max(worst, Math.abs(a - r) / r);
  }
  return worst;
}

function wrap360(lonDeg: number): number {
  return ((lonDeg % 360) + 360) % 360;
}

/** The engine's lat/lon frame: east-positive, z negative-sine. */
function unitFor(latDeg: number, lonDeg: number, out: THREE.Vector3): THREE.Vector3 {
  const lat = THREE.MathUtils.degToRad(latDeg);
  const lon = THREE.MathUtils.degToRad(lonDeg);
  return out.set(
    Math.cos(lat) * Math.cos(lon), Math.sin(lat), -Math.cos(lat) * Math.sin(lon));
}

/** The inverse of `unitFor`: engine degrees off a unit direction. */
function latLonOfUnit(v: THREE.Vector3): [number, number] {
  const lat = THREE.MathUtils.radToDeg(Math.asin(THREE.MathUtils.clamp(v.y, -1, 1)));
  const lon = THREE.MathUtils.radToDeg(Math.atan2(-v.z, v.x));
  return [lat, lon];
}

export interface HexOverlayView {
  /** Registry `default_h3_resolution`, or null on a body canon gives none. */
  baseRes: number | null;
  radiusM: number;
  /**
   * The row's own ellipsoid semi-axes, metres, or null where it carries none.
   *
   * Read only to decide whether this body's surface is near enough its datum
   * sphere to drape a spherical grid on at all (`SHAPE_DEPARTURE_LIMIT`).
   * Nothing here is drawn on an ellipsoid.
   */
  ellipsoidAxesM: readonly number[] | null;
  /** The camera's own focus, in engine degrees: where the descent is aimed. */
  latDeg: number;
  lonDeg: number;
  /**
   * The SUB-CAMERA point, body-fixed engine degrees, and the eye's own distance
   * from the body centre.
   *
   * The cap is centred and sized on these and not on the focus: at every band
   * above SURFACE the pitch is shallow enough that the eye stands tens of
   * degrees of arc away from the ground it is looking at, and the visible disc
   * is centred under the EYE.
   */
  camLatDeg: number;
  camLonDeg: number;
  camRadiusM: number;
  altitudeM: number;
  viewportH: number;
  fovDeg: number;
  aspect: number;
  /** The cell the descent affordance sits in, in engine degrees. */
  anchor: { lat: number; lon: number } | null;
  /**
   * Metres above the datum under this body-fixed lat/lon, or null where
   * nothing loaded reaches it: the ground the grid is drawn against, which is
   * `hexGroundHeightAt`'s pick.
   */
  heightAt(latDeg: number, lonDeg: number): number | null;
  /**
   * Bumped by the app every time the tile pyramid mounts or unmounts a leaf.
   *
   * A vertex draped against `heightAt` at one version can read a different
   * height once a finer tile streams in under the same lat/lon; the pose the
   * camera holds has not changed, so the camera-motion half of `syncLayer`'s
   * own staleness check never catches it on its own.
   */
  heightVersion: number;
  /**
   * Unit vector toward the sun, body-fixed: the same frame `latLonToVec3`
   * places a cell's own direction in, since `detailGroup` (the overlay's
   * parent) carries the body's rotation and the grid does not. `atlas.ts`'s
   * `syncSolar` is the one place this is computed for every focused body
   * unconditionally, so the overlay reads its `_sunFixed` rather than
   * deriving a second sun that could disagree with the ground under it.
   */
  sunDirFixed: THREE.Vector3;
}

export interface HexOverlayLayerDebug {
  res: number;
  weight: number;
  cells: number;
  edges: number;
  segments: number;
  vertices: number;
  subdivisions: number;
  pentagons: number;
}

export interface HexOverlayDebug {
  ready: boolean;
  baseRes: number | null;
  drawnRes: number | null;
  resStep: number;
  suppressed: boolean;
  float: boolean;
  hardSwap: boolean;
  unlit: boolean;
  /** True where the body's own shape puts it past `SHAPE_DEPARTURE_LIMIT`. */
  shapeExcluded: boolean;
  /** What that limit was read against, and the limit itself. */
  shapeDeparture: number;
  shapeLimit: number;
  /** `setDebug({ forceShape: true })`: the grid back on an excluded body. */
  forceShape: boolean;
  /** `setDebug({ legacy: true })`: the pre-fix cap and subdivision back. */
  legacy: boolean;
  datumHorizon: boolean;
  markerDatum: boolean;
  /** The cell the cursor is on, or null. */
  hoverCell: string | null;
  /** Whether the wash for it is on the pane this frame. */
  hoverDrawn: boolean;
  cells: number;
  edges: number;
  /** Emitted line segments: adaptive, so no longer `edges * subdivisions`. */
  segments: number;
  vertices: number;
  subdivisions: number;
  /** Segments that hit `ARC_MAX_DEPTH` still sagging past the tolerance. */
  sagCapped: number;
  /** Ceiling hits over a level boundary's step, which no subdivision closes. */
  stepCapped: number;
  /** Draped points with no height source under them, on the datum float. */
  floatPts: number;
  sagToleranceM: number;
  pentagons: number;
  biasM: number;
  capHalfAngleDeg: number;
  /** The cap's own centre, which is the sub-camera point of the build. */
  latDeg: number;
  lonDeg: number;
  camRadiusM: number;
  /** The sub-camera point of the build, and the horizon sphere sized under it. */
  camLatDeg: number;
  camLonDeg: number;
  horizonRadiusM: number;
  /** The height-source revision the view carries right now. */
  heightVersion: number;
  /**
   * The revision the DRAWN layer was draped against, and whether a build is in
   * flight. A build spans frames and drapes each edge live, so a layer
   * committed while tiles were still landing carries two different grounds:
   * anything reading the drape has to wait for these to agree.
   */
  drapedHeightVersion: number;
  building: boolean;
  altitudeM: number;
  pxAcross: number;
  acrossFlatsM: number;
  anchorCell: string | null;
  /** The neighbour resolution fading in beside `drawnRes`, or null when none is. */
  fade: HexOverlayLayerDebug | null;
}

export interface HexOverlayConfig {
  /** Called when a sliced build lands, so a still frame redraws with it. */
  onBuild?: () => void;
}

export interface HexOverlayHandle {
  readonly object: THREE.Group;
  /** Rebuild if the view moved enough to change what is drawn. */
  update(view: HexOverlayView): void;
  /**
   * The resolution `update()` would draw for this view, without paying for
   * the cap or the geometry.
   *
   * The tile decode's claim texture reads this so it bakes against the same
   * rung the overlay draws, off the one aggregate-upward rule `drawnResolution`
   * already is, rather than a second copy of it living in the decode path.
   */
  resolutionAt(view: HexOverlayView): number | null;
  /** The cell under a piece of ground, at the resolution now drawn. */
  pick(latDeg: number, lonDeg: number): string | null;
  /** The cell the descent affordance sits in, at the resolution now drawn. */
  anchorCell(): string | null;
  /**
   * The cell under the cursor, washed so the eye can see which one a click
   * would take. `null` takes the wash off.
   *
   * A cell id rather than a lat/lon: the caller resolves the ground it is
   * pointing at against the same `pick` a click uses, so the cell that lights
   * up and the cell that answers cannot be two different cells.
   */
  setHoverCell(cell: string | null): void;
  cellCentre(cell: string): { lat: number; lon: number } | null;
  /** Mean across-flats and area of a drawn cell, derived not stored. */
  cellSize(): { acrossFlatsM: number; areaKm2: number } | null;
  /**
   * Whether this ground lies inside a cell the primary layer actually DREW.
   *
   * `pick` answers at the drawn resolution for any lat/lon on the body,
   * drawn or not; this is the committed fill's own membership, which is what
   * `descent-dojo --hex`'s reach reading needs to tell ground with a boundary
   * on it from ground the cap stopped short of.
   */
  covers(latDeg: number, lonDeg: number): boolean;
  debug(): HexOverlayDebug;
  setDebug(next: {
    resStep?: number | null; suppress?: boolean; float?: boolean; hardSwap?: boolean;
    /** `true` puts the flat, day/night-blind material back (`--inject hex-unlit`). */
    unlit?: boolean;
    /** `true` draws the grid on a body past `SHAPE_DEPARTURE_LIMIT` anyway. */
    forceShape?: boolean;
    /** Fraction of the derived cap to commit: the reach reading's own control. */
    capScale?: number | null;
    /** Metres to sink every draped vertex by: the survival reading's control. */
    sinkM?: number | null;
    /** `true` puts the pre-fix cap and fixed subdivision back (`--inject hex-legacy`). */
    legacy?: boolean;
    /** `true` sizes the horizon off the datum sphere again (`--inject hex-datum-horizon`). */
    datumHorizon?: boolean;
    /** `true` puts the anchor marker back on the datum float (`--inject marker-datum`). */
    markerDatum?: boolean;
  }): void;
  dispose(): void;
}

interface BuildJob {
  view: HexOverlayView;
  res: number;
  capRad: number;
  float: boolean;
  /** The fill's frontier, read by `cursor` and grown as it is read. */
  queue: string[];
  visited: Set<string>;
  near: Set<string>;
  set: string[];
  cursor: number;
  stage: 'filter' | 'edges';
  seen: Set<string>;
  pts: number[];
  edgeCount: number;
  /** Emitted segments, and the deepest bisection any one edge needed. */
  segments: number;
  maxDepth: number;
  sagCapped: number;
  /** Ceiling hits whose figure is a level boundary's step, not a sag. */
  stepCapped: number;
  /** Draped points no height source answered for, so they took the float. */
  floatPts: number;
  /** The sag a chord may carry below the ground, and the lift every vertex takes. */
  tolM: number;
  biasM: number;
  pentagons: number;
}

interface Layer {
  material: THREE.LineBasicMaterial;
  geometry: THREE.BufferGeometry | null;
  /** The committed fill's own cells, which is what `covers` answers from. */
  cellSet: Set<string>;
  lines: THREE.LineSegments | null;
  job: BuildJob | null;
  cells: number;
  edges: number;
  segments: number;
  vertices: number;
  subdivisions: number;
  sagCapped: number;
  stepCapped: number;
  floatPts: number;
  tolM: number;
  biasM: number;
  pentagons: number;
  capDeg: number;
  acrossFlatsM: number;
  builtLat: number;
  builtLon: number;
  builtCap: number;
  builtRes: number;
  builtBody: number;
  builtHeightVersion: number;
}

/**
 * The grid.
 *
 * h3-js is 82 KB gzipped and only the Atlas needs it, so it loads with the
 * overlay the way `plotName.ts` loads it with the tier. Until it lands nothing
 * is drawn, which is the same as a body with no resolution.
 */
export function createHexOverlay(config: HexOverlayConfig = {}): HexOverlayHandle {
  const object = new THREE.Group();
  object.name = 'hex-overlay';

  let h3: H3 | null = null;
  let disposed = false;
  // Imported at construction, which is when the Atlas opens, and touched once
  // here: the first call into h3 pays the module's own table setup, and a
  // descent frame is not where that belongs.
  void import('h3-js').then((m) => {
    if (disposed) return;
    m.latLngToCell(0, 0, 0);
    h3 = m;
  });

  // Fed from `HexOverlayView.sunDirFixed` every `update()`, in the same
  // body-fixed frame `position` (the raw attribute, before `detailGroup`'s
  // own rotation) is written in - so a vertex shader can dot the two with no
  // normal matrix and no second sun.
  const hexSunU: { value: THREE.Vector3 } = { value: new THREE.Vector3(0, 0, 0) };
  /** `setHexDebug({ unlit: true })`: 1 bypasses the Lambert term entirely. */
  const hexUnlitU: { value: number } = { value: 0 };

  /**
   * Give a basic (unlit-by-definition) line or marker material the ground's
   * own day/night term: a vertex's outward datum direction is `position`
   * itself before it is scaled by radius, so `normalize(position)` recovers
   * it exactly with no extra attribute.
   */
  function installHexShading(material: THREE.LineBasicMaterial): void {
    material.onBeforeCompile = (shader): void => {
      shader.uniforms['uHexSunDir'] = hexSunU;
      shader.uniforms['uHexUnlit'] = hexUnlitU;
      shader.vertexShader = `uniform vec3 uHexSunDir;\nvarying float vHexLit;\n${shader.vertexShader}`
        .replace('#include <begin_vertex>',
          '#include <begin_vertex>\n'
          + '  vHexLit = max(dot(normalize(position), uHexSunDir), 0.0);');
      shader.fragmentShader = `uniform float uHexUnlit;\nvarying float vHexLit;\n${shader.fragmentShader}`
        .replace('#include <opaque_fragment>',
          `  outgoingLight *= mix(max(vHexLit, ${NIGHT_FLOOR.toFixed(2)}), 1.0, uHexUnlit);\n`
          + '#include <opaque_fragment>');
    };
    material.customProgramCacheKey = (): string => 'fxciv-hex-lit';
    material.needsUpdate = true;
  }

  function makeLayer(): Layer {
    const material = new THREE.LineBasicMaterial({
      color: 0x88e8ff, transparent: true, opacity: 0.85, depthWrite: false,
    });
    installHexShading(material);
    return {
      material,
      geometry: null,
      cellSet: new Set<string>(),
      lines: null,
      job: null,
      cells: 0,
      edges: 0,
      segments: 0,
      vertices: 0,
      subdivisions: 1,
      sagCapped: 0,
      stepCapped: 0,
      floatPts: 0,
      tolM: DEPTH_BIAS_M,
      biasM: DEPTH_BIAS_M,
      pentagons: 0,
      capDeg: 0,
      acrossFlatsM: 0,
      builtLat: NaN,
      builtLon: NaN,
      builtCap: 0,
      builtRes: -1,
      builtBody: 0,
      builtHeightVersion: -1,
    };
  }

  // Two layers: the resolution `drawnResolution` names, and whichever
  // neighbour `resolutionFade` says is fading in beside it. The second is
  // built and disposed as the camera moves in and out of a fade window.
  const primary = makeLayer();
  let secondary: Layer | null = null;

  // One marker, not twenty-five: the affordance that says which cell descends.
  // The ring this replaced drew an additive sphere per cell, which saturated
  // into one white blob once the camera closed in.
  //
  // Its own mesh is a small ball, so a per-vertex Lambert term off its LOCAL
  // normal would shade it toward `uHexSunDir` regardless of which side of the
  // body it sits on - the exact bug this file exists to fix, at marker scale.
  // `update()` instead dots the marker's own body-fixed PLACEMENT against the
  // sun each frame and scales `markerColor` by hand.
  const markerGeo = new THREE.SphereGeometry(1, 10, 8);
  const markerColor = new THREE.Color(0xffd17a);
  const markerMat = new THREE.MeshBasicMaterial({
    color: markerColor.clone(), transparent: true, opacity: 0.95, depthTest: false,
  });
  const marker = new THREE.Mesh(markerGeo, markerMat);
  marker.renderOrder = 5;
  marker.userData['atlasAnchorCell'] = true;
  marker.visible = false;
  object.add(marker);
  const _markerDir = new THREE.Vector3();

  // The cursor's own cell, washed rather than outlined. Every boundary on the
  // pane is already drawn in this colour, so one redrawn brighter is a line
  // among lines; a wash over one cell's ground is the cell.
  //
  // Depth-tested by nothing, for the marker's reason: a highlight the relief
  // in front of it can swallow answers the cursor only on flat ground. Shaded
  // by hand against the sun, again like the marker - its own triangles face
  // every which way and a Lambert term off them would light the night side.
  const hoverColor = new THREE.Color(0x88e8ff);
  const hoverMat = new THREE.MeshBasicMaterial({
    color: hoverColor.clone(), transparent: true, opacity: 0.3,
    depthTest: false, depthWrite: false, side: THREE.DoubleSide,
  });
  const hoverMesh = new THREE.Mesh(new THREE.BufferGeometry(), hoverMat);
  hoverMesh.name = 'hex-hover-cell';
  hoverMesh.renderOrder = 3;
  hoverMesh.visible = false;
  object.add(hoverMesh);
  const _hoverDir = new THREE.Vector3();
  const _hoverA = new THREE.Vector3();
  const _hoverB = new THREE.Vector3();
  const _hoverC = new THREE.Vector3();
  let hoverCell: string | null = null;
  let hoverBuiltKey = '';

  /**
   * Rebuild the wash when the cell, the ground under it or the body changes.
   *
   * A triangle fan off the cell's own centre, every vertex draped the way the
   * boundary lines are, so the wash sits on the same ground they do.
   */
  function syncHover(view: HexOverlayView): void {
    const lib = h3;
    const cell = hoverCell;
    if (!lib || cell === null || primary.builtRes < 0
      || lib.getResolution(cell) !== primary.builtRes) {
      hoverMesh.visible = false;
      return;
    }
    const key = `${cell}|${view.heightVersion}|${Math.round(view.radiusM)}`
      + `|${debug.float ? 1 : 0}|${primary.biasM}`;
    if (key !== hoverBuiltKey) {
      hoverBuiltKey = key;
      const boundary = lib.cellToBoundary(cell);
      const centre = lib.cellToLatLng(cell);
      unitFor(centre[0], wrap360(centre[1]), _hoverC);
      drapedPoint(_hoverC, view, debug.float, primary.biasM);
      const pts: number[] = [];
      for (let i = 0; i < boundary.length; i++) {
        const p0 = boundary[i]!;
        const p1 = boundary[(i + 1) % boundary.length]!;
        unitFor(p0[0]!, wrap360(p0[1]!), _hoverA);
        unitFor(p1[0]!, wrap360(p1[1]!), _hoverB);
        drapedPoint(_hoverA, view, debug.float, primary.biasM);
        drapedPoint(_hoverB, view, debug.float, primary.biasM);
        pts.push(_hoverC.x, _hoverC.y, _hoverC.z,
          _hoverA.x, _hoverA.y, _hoverA.z, _hoverB.x, _hoverB.y, _hoverB.z);
      }
      hoverMesh.geometry.dispose();
      const geo = new THREE.BufferGeometry();
      geo.setAttribute('position', new THREE.BufferAttribute(new Float32Array(pts), 3));
      hoverMesh.geometry = geo;
    }
    hoverMesh.visible = true;
    const c = lib.cellToLatLng(cell);
    unitFor(c[0], wrap360(c[1]), _hoverDir);
    const lit = debug.unlit
      ? 1 : Math.max(_hoverDir.normalize().dot(view.sunDirFixed), NIGHT_FLOOR);
    hoverMat.color.copy(hoverColor).multiplyScalar(lit);
  }

  const debug: {
    resStep: number; suppress: boolean; float: boolean; hardSwap: boolean; unlit: boolean;
    forceShape: boolean; capScale: number; sinkM: number; legacy: boolean;
    datumHorizon: boolean;
    markerDatum: boolean;
  } = {
    resStep: 0, suppress: false, float: false, hardSwap: false, unlit: false,
    forceShape: false, capScale: 1, sinkM: 0, legacy: false, datumHorizon: false,
    markerDatum: false,
  };

  let pxAcross = 0;
  let anchor: string | null = null;
  let liveBase: number | null = null;
  let liveAlt = 0;
  let liveCamRadiusM = 0;
  let liveCamLatDeg = 0;
  let liveCamLonDeg = 0;
  let liveHorizonRadiusM = 0;
  let liveHeightVersion = -1;
  let liveDeparture = 0;
  let liveExcluded = false;

  /**
   * How long one frame may spend on the grid, shared across both layers.
   *
   * The whole build used to run inside the frame that noticed the view had
   * moved: 1,147 cells at the ORBIT-to-REGIONAL rung step is 83 ms, over the
   * descent's own 50 ms frame budget, and it landed on the leg already paying
   * for a tile burst. It is the same work, cut into slices this size, with the
   * grid that is up staying up until the new one is whole. One shared deadline
   * for both layers keeps a fading pair no more expensive, worst case, than
   * one layer was.
   */
  const BUILD_BUDGET_MS = 3;
  /** Cells per clock read: enough that `performance.now()` is not the cost. */
  const FILTER_CHUNK = 512;
  // Adaptive subdivision puts up to 2^ARC_MAX_DEPTH segments and as many
  // height samples behind one edge, so the clock is read far more often than
  // the fixed-count build needed.
  const EDGE_CHUNK = 8;

  function clearLayer(layer: Layer): void {
    if (layer.lines) object.remove(layer.lines);
    layer.geometry?.dispose();
    layer.geometry = null;
    layer.lines = null;
    layer.job = null;
    layer.cellSet.clear();
    layer.cells = 0;
    layer.edges = 0;
    layer.segments = 0;
    layer.vertices = 0;
    layer.sagCapped = 0;
    layer.stepCapped = 0;
    layer.floatPts = 0;
    layer.pentagons = 0;
    layer.builtRes = -1;
  }

  function disposeSecondary(): void {
    if (!secondary) return;
    clearLayer(secondary);
    secondary.material.dispose();
    secondary = null;
  }

  /**
   * Open a build. Nothing is drawn from it until `commitBuild` swaps it in.
   *
   * The visible set is a spherical CAP, and `polygonToCells` cannot take one:
   * a cap holding a pole either throws or comes back short without saying so
   * (a 50 degree cap on the south pole returns 77 of 1,050 cells). A fill out
   * from the centre cell, stopped at the great-circle angle the cap actually
   * subtends, is exact everywhere and needs no polygon, and it grows a slice
   * at a time. A `gridDisk` out to the cap's ring count cannot: it is one
   * call, and h3 answers it by a pentagon-safe search wherever the disk can
   * reach a pentagon, which at ORBIT and REGIONAL is every coarse rung - 58 ms
   * at res 3 on Mars, in the frame that asked. The node side of `--hex` checks
   * the set with `polygonToCells`, which is a different algorithm for the same
   * answer away from the poles.
   */
  function startBuild(view: HexOverlayView, res: number, capRad: number, float: boolean): BuildJob {
    const lib = h3!;
    // The fill runs out from the ground under the EYE, not from the focus: the
    // cap is centred there and a fill seeded anywhere else has to cross ground
    // the filter rejects to reach it.
    const centre = lib.latLngToCell(view.camLatDeg, wrap180HalfOpen(view.camLonDeg), res);
    return {
      view: { ...view },
      res,
      capRad,
      float,
      // The first ring is unconditional: below the CLAIM band the cap is
      // smaller than one cell and the filter would leave the ground the camera
      // is standing on with no boundary at all.
      near: new Set(lib.gridDisk(centre, 1)),
      queue: [centre],
      visited: new Set([centre]),
      set: [],
      cursor: 0,
      stage: 'filter',
      seen: new Set(),
      pts: [],
      edgeCount: 0,
      segments: 0,
      maxDepth: 0,
      sagCapped: 0,
      stepCapped: 0,
      floatPts: 0,
      pentagons: 0,
      tolM: sagToleranceM(view),
      // Twice the sag allowance, not once: a chord that sags the whole
      // allowance then clears the ground by the OTHER `tolM` rather than by
      // `DEPTH_BIAS_M`, and half a metre at a 4,700 km eye distance is far
      // under what the depth buffer can resolve there.
      biasM: DEPTH_BIAS_M + 2 * sagToleranceM(view),
    };
  }

  /**
   * The sag a drawn chord may carry below the ground under it, metres.
   *
   * A quarter of a pixel of the frame, so the allowance shrinks with the
   * camera, but never past the ceiling `BIAS_MAX_RADII` sets: the vertex bias
   * has to cover this whole figure or the chord between two draped vertices
   * dips under the mesh and the depth test cuts the line there, which is what
   * fragmented every cell at REGIONAL.
   */
  function sagToleranceM(view: HexOverlayView): number {
    const mpp = (2 * Math.tan(THREE.MathUtils.degToRad(view.fovDeg) / 2)
      * Math.max(1, view.altitudeM)) / Math.max(1, view.viewportH);
    return Math.max(DEPTH_BIAS_M, Math.min(SAG_PX * mpp, BIAS_MAX_RADII * view.radiusM));
  }

  /**
   * A vertex's own draped position: the height source, or the datum float.
   *
   * Returns whether a height source answered, which the arc test needs: a
   * point on the float sits kilometres off the ground beside it.
   */
  function drapedPoint(
    m: THREE.Vector3, view: HexOverlayView, float: boolean, biasM: number,
  ): boolean {
    const [latDeg, lonDeg] = latLonOfUnit(m);
    const h = float ? null : view.heightAt(latDeg, lonDeg);
    const r = h === null
      ? view.radiusM * LIFT
      : view.radiusM + h + biasM - debug.sinkM;
    m.multiplyScalar(r);
    return h !== null;
  }

  // One pair of scratch vectors per bisection level, so an edge that needs
  // eight of them allocates nothing in the frame that draws it.
  const _arcU: THREE.Vector3[] = [];
  const _arcP: THREE.Vector3[] = [];
  for (let i = 0; i <= ARC_MAX_DEPTH; i++) {
    _arcU.push(new THREE.Vector3());
    _arcP.push(new THREE.Vector3());
  }
  const _chord = new THREE.Vector3();
  // The off-midpoint probes are read and thrown away before the recursion
  // touches them again, so one pair covers every depth.
  const _probeU = new THREE.Vector3();
  const _probeP = new THREE.Vector3();

  /**
   * Emit one cell edge as a polyline that stays above the ground it crosses.
   *
   * Bisected while the arc's own midpoint stands more than `j.tolM` above the
   * straight chord that would be drawn instead - which is one criterion for
   * both the sphere's curvature and the relief on it, since `drapedPoint` puts
   * the midpoint on the same height field the tile mesh is displaced by. A
   * fixed segment count could not do this: two subdivisions was a quarter
   * pixel of curvature at REGIONAL and thirty kilometres of ground between
   * vertices, and every crater rim in between rose through the line.
   */
  /**
   * How far the draped ground at `t` along the arc stands above the chord.
   *
   * The midpoint alone is blind to a ridge anywhere else along the span: on
   * Mars at LOCAL a chord ran two kilometres over ground meshed every 500 m,
   * its own middle happened to sit low, and a quarter of the grid went under
   * the terrain the midpoint test had passed.
   */
  function arcSagAt(
    j: BuildJob, ua: THREE.Vector3, pa: THREE.Vector3,
    ub: THREE.Vector3, pb: THREE.Vector3, t: number, sourced: boolean,
  ): number {
    slerp(ua, ub, t, _probeU);
    _probeP.copy(_probeU);
    if (drapedPoint(_probeP, j.view, j.float, j.biasM) !== sourced) return 0;
    return _probeP.length() - Math.hypot(
      pa.x + (pb.x - pa.x) * t, pa.y + (pb.y - pa.y) * t, pa.z + (pb.z - pa.z) * t);
  }

  function emitArc(
    j: BuildJob, ua: THREE.Vector3, pa: THREE.Vector3, sa: boolean,
    ub: THREE.Vector3, pb: THREE.Vector3, sb: boolean, depth: number,
  ): void {
    const mu = _arcU[depth]!;
    const mp = _arcP[depth]!;
    slerp(ua, ub, 0.5, mu);
    mp.copy(mu);
    const sm = drapedPoint(mp, j.view, j.float, j.biasM);
    if (!sm) j.floatPts++;
    // Where the pyramid's coverage ends inside the cap, one end of an edge is
    // on measured ground and the other on the datum float kilometres away:
    // that figure is not a sag, and bisecting toward it burnt every level of
    // depth on one segment and reported the ceiling as a fault.
    const sag = sm !== sa || sm !== sb ? 0 : Math.max(
      mp.length() - _chord.copy(pa).add(pb).multiplyScalar(0.5).length(),
      arcSagAt(j, ua, pa, ub, pb, 0.25, sm),
      arcSagAt(j, ua, pa, ub, pb, 0.75, sm));
    if (depth < ARC_MAX_DEPTH) {
      if (sag > j.tolM) {
        emitArc(j, ua, pa, sa, mu, mp, sm, depth + 1);
        emitArc(j, mu, mp, sm, ub, pb, sb, depth + 1);
        return;
      }
    } else if (sag > j.biasM) {
      // Past the BIAS, not past the tolerance: a chord that sags the whole
      // allowance still clears the ground by the rest of the bias, and only a
      // sag deeper than the lift itself puts the line under the mesh.
      //
      // A STEP is not a sag: across a level boundary the drawn ground jumps by
      // whatever the two tiles disagree by, which is what their own skirts
      // hide, and halving the span toward it converges on half the step rather
      // than on zero. Counted apart, so the ceiling still means what it says.
      if (Math.abs(pa.length() - pb.length()) > j.biasM) j.stepCapped++;
      else j.sagCapped++;
    }
    j.pts.push(pa.x, pa.y, pa.z, pb.x, pb.y, pb.z);
    j.segments++;
    if (depth > j.maxDepth) j.maxDepth = depth;
  }

  /**
   * `--inject hex-legacy`'s other half: the pre-fix fixed segment count.
   *
   * The count came off the sphere's own curvature against half a pixel and
   * knew nothing about the relief between two vertices - two segments over a
   * 36 km edge at REGIONAL, so every ridge inside 18 km of ground rose through
   * the chord and the depth test cut the line where it did.
   */
  function emitUniform(j: BuildJob, a: THREE.Vector3, b: THREE.Vector3): void {
    const lib = h3!;
    const w = cellAcrossFlatsM(lib, j.res, j.view.radiusM);
    const mpp = (2 * Math.tan(THREE.MathUtils.degToRad(j.view.fovDeg) / 2)
      * Math.max(1, j.view.altitudeM)) / Math.max(1, j.view.viewportH);
    const arc = (w / Math.sqrt(3)) / j.view.radiusM;
    const sagOne = j.view.radiusM * (1 - Math.cos(arc / 2));
    const subs = THREE.MathUtils.clamp(
      Math.ceil(Math.sqrt(sagOne / Math.max(1e-3, 0.5 * mpp))), 1, 64);
    const m = _arcU[0]!;
    for (let sub = 0; sub < subs; sub++) {
      slerp(a, b, sub / subs, m);
      drapedPoint(m, j.view, j.float, DEPTH_BIAS_M);
      j.pts.push(m.x, m.y, m.z);
      slerp(a, b, (sub + 1) / subs, m);
      drapedPoint(m, j.view, j.float, DEPTH_BIAS_M);
      j.pts.push(m.x, m.y, m.z);
      j.segments++;
    }
  }

  /** Spend the shared slice on one job, and commit it if it finishes. */
  function stepBuildJob(j: BuildJob, deadline: number): boolean {
    const lib = h3;
    if (!lib) return false;
    const a = new THREE.Vector3();
    const b = new THREE.Vector3();
    const pa = new THREE.Vector3();
    const pb = new THREE.Vector3();
    const lat0 = j.view.camLatDeg;
    const lon0 = wrap180HalfOpen(j.view.camLonDeg);
    do {
      if (j.stage === 'filter') {
        const end = Math.min(j.queue.length, j.cursor + FILTER_CHUNK);
        for (; j.cursor < end; j.cursor++) {
          const cell = j.queue[j.cursor]!;
          // The cap already carries CAP_MARGIN's slack past the frame, so a
          // cell whose centre falls outside it is out, and the fill stops
          // there rather than crossing it.
          if (!j.near.has(cell)) {
            const [clat, clon] = lib.cellToLatLng(cell);
            if (greatCircleRad(lat0, lon0, clat, clon) > j.capRad) continue;
          }
          j.set.push(cell);
          if (lib.isPentagon(cell)) j.pentagons++;
          if (j.set.length >= CELL_CEILING) { j.cursor = j.queue.length; break; }
          for (const n of lib.gridDisk(cell, 1)) {
            if (!j.visited.has(n)) { j.visited.add(n); j.queue.push(n); }
          }
        }
        if (j.cursor >= j.queue.length) {
          j.stage = 'edges';
          j.cursor = 0;
        }
        continue;
      }
      const end = Math.min(j.set.length, j.cursor + EDGE_CHUNK);
      for (; j.cursor < end; j.cursor++) {
        const boundary = lib.cellToBoundary(j.set[j.cursor]!);
        for (let i = 0; i < boundary.length; i++) {
          const p0 = boundary[i]!;
          const p1 = boundary[(i + 1) % boundary.length]!;
          const key = edgeKey(p0, p1);
          if (j.seen.has(key)) continue;
          j.seen.add(key);
          j.edgeCount++;
          unitFor(p0[0]!, wrap360(p0[1]!), a);
          unitFor(p1[0]!, wrap360(p1[1]!), b);
          const sa = drapedPoint(pa.copy(a), j.view, j.float, j.biasM);
          const sb = drapedPoint(pb.copy(b), j.view, j.float, j.biasM);
          if (!sa) j.floatPts++;
          if (!sb) j.floatPts++;
          if (debug.legacy) emitUniform(j, a, b);
          else emitArc(j, a, pa, sa, b, pb, sb, 0);
        }
      }
      if (j.cursor >= j.set.length) return true;
    } while (performance.now() < deadline);
    return false;
  }

  /** Swap the finished build into a layer, in one frame, and report it. */
  function commitLayer(layer: Layer, name: string, j: BuildJob): void {
    const lib = h3;
    if (!lib) return;
    clearLayer(layer);
    layer.geometry = new THREE.BufferGeometry();
    layer.geometry.setAttribute('position',
      new THREE.BufferAttribute(new Float32Array(j.pts), 3));
    layer.lines = new THREE.LineSegments(layer.geometry, layer.material);
    layer.lines.name = name;
    layer.lines.renderOrder = 4;
    object.add(layer.lines);

    layer.cellSet = new Set(j.set);
    layer.cells = j.set.length;
    layer.edges = j.edgeCount;
    layer.segments = j.segments;
    layer.vertices = j.pts.length / 3;
    layer.subdivisions = j.maxDepth > 0 ? 2 ** j.maxDepth : 1;
    layer.sagCapped = j.sagCapped;
    layer.stepCapped = j.stepCapped;
    layer.floatPts = j.floatPts;
    layer.tolM = j.tolM;
    layer.biasM = j.biasM;
    layer.pentagons = j.pentagons;
    layer.capDeg = THREE.MathUtils.radToDeg(j.capRad);
    layer.acrossFlatsM = cellAcrossFlatsM(lib, j.res, j.view.radiusM);
    layer.builtLat = j.view.camLatDeg;
    layer.builtLon = j.view.camLonDeg;
    layer.builtCap = j.capRad;
    layer.builtRes = j.res;
    layer.builtBody = Math.round(j.view.radiusM);
    layer.builtHeightVersion = j.view.heightVersion;
    config.onBuild?.();
  }

  /** Start, continue or commit a layer's build toward `res`, sharing `deadline`. */
  function syncLayer(
    layer: Layer, name: string, view: HexOverlayView, res: number, capRad: number,
    deadline: number,
  ): void {
    const lib = h3;
    if (!lib) return;
    const bodyKey = Math.round(view.radiusM);
    const moved = !Number.isFinite(layer.builtLat)
      || res !== layer.builtRes
      || bodyKey !== layer.builtBody
      || Math.abs(capRad - layer.builtCap) > layer.builtCap * 0.1
      || greatCircleRad(layer.builtLat, layer.builtLon, view.camLatDeg, view.camLonDeg)
        > (cellAcrossFlatsM(lib, res, view.radiusM) / view.radiusM) / 3
      // A vertex's own draped height can go stale with the pose held still,
      // once a tile mount under the cap refines the ground it was drawn on.
      || view.heightVersion !== layer.builtHeightVersion;
    // One build at a time per layer, restarted only where the view has left
    // the one in flight behind: a descent moves every frame, and a job
    // restarted every frame never finishes.
    const stale = layer.job !== null
      && (layer.job.res !== res
        || layer.job.view.radiusM !== view.radiusM
        || Math.abs(capRad - layer.job.capRad) > layer.job.capRad * 0.5
        || greatCircleRad(
          layer.job.view.camLatDeg, layer.job.view.camLonDeg, view.camLatDeg, view.camLonDeg)
          > layer.job.capRad * 0.5);
    if (moved && (layer.job === null || stale)) {
      layer.job = startBuild(view, res, capRad, debug.float);
    }
    if (layer.job) {
      const done = stepBuildJob(layer.job, deadline);
      if (done) { commitLayer(layer, name, layer.job); layer.job = null; }
    }
  }

  return {
    object,

    update(view: HexOverlayView): void {
      if (disposed) return;
      hexSunU.value.copy(view.sunDirFixed);
      hexUnlitU.value = debug.unlit ? 1 : 0;
      const rawBase = view.baseRes === null
        ? null
        : THREE.MathUtils.clamp(view.baseRes + debug.resStep, 0, 15);
      // Reported even while suppressed: `hexDebug.baseRes` is what the row
      // says regardless of whether the grid is drawn from it right now.
      liveBase = rawBase;
      liveAlt = view.altitudeM;
      liveCamRadiusM = view.camRadiusM;
      liveHeightVersion = view.heightVersion;
      liveCamLatDeg = view.camLatDeg;
      liveCamLonDeg = view.camLonDeg;
      liveDeparture = shapeDepartureFrac(view.radiusM, view.ellipsoidAxesM);
      liveExcluded = !debug.forceShape && liveDeparture > SHAPE_DEPARTURE_LIMIT;
      const base = debug.suppress || liveExcluded ? null : rawBase;
      if (!h3 || base === null) {
        clearLayer(primary);
        disposeSecondary();
        marker.visible = false;
        anchor = null;
        return;
      }
      const deadline = performance.now() + BUILD_BUDGET_MS;
      // Sized off the rung that is about to be drawn, since the slack past the
      // horizon is one CELL wide.
      const capRes = debug.hardSwap
        ? drawnResolution(h3, base, view.radiusM, view.altitudeM, view.viewportH, view.fovDeg)
        : resolutionFade(
          h3, base, view.radiusM, view.altitudeM, view.viewportH, view.fovDeg).resA;
      liveHorizonRadiusM = debug.datumHorizon ? view.radiusM : horizonRadiusM(view);
      const capRad = debug.capScale * (debug.legacy
        ? legacyCapHalfAngleRad(view.radiusM, view.altitudeM, view.fovDeg, view.aspect)
        : capHalfAngleRad(
          view.radiusM, view.camRadiusM, cellAcrossFlatsM(h3, capRes, view.radiusM),
          liveHorizonRadiusM));
      // The control centres the fill where the pre-fix build centred it too:
      // the cap and its centre were one fault and reproducing half of it would
      // read as neither.
      const v: HexOverlayView = debug.legacy
        ? { ...view, camLatDeg: view.latDeg, camLonDeg: view.lonDeg }
        : view;
      // `hardSwap` reproduces the pre-cross-fade behaviour on purpose: one
      // resolution, one layer, a pop the moment the boundary is crossed.
      const fade = debug.hardSwap
        ? { resA: drawnResolution(h3, base, view.radiusM, view.altitudeM, view.viewportH, view.fovDeg),
          resB: -1, weightA: 1, weightB: 0 }
        : resolutionFade(h3, base, view.radiusM, view.altitudeM, view.viewportH, view.fovDeg);

      syncLayer(primary, 'hex-boundaries', v, fade.resA, capRad, deadline);
      primary.material.opacity = 0.85 * fade.weightA;
      if (primary.lines) primary.lines.visible = fade.weightA > 1e-4;

      if (fade.weightB > 1e-4 && fade.resB !== fade.resA) {
        if (!secondary) secondary = makeLayer();
        syncLayer(secondary, 'hex-boundaries-fade', v, fade.resB, capRad, deadline);
        secondary.material.opacity = 0.85 * fade.weightB;
        if (secondary.lines) secondary.lines.visible = true;
      } else if (secondary) {
        disposeSecondary();
      }

      if (primary.acrossFlatsM > 0) {
        pxAcross = pixelsAcross(
          primary.acrossFlatsM, view.altitudeM, view.viewportH, view.fovDeg);
      }
      // The cell the marker marks is the one that is DRAWN, not the one the
      // altitude has just selected: a build takes a few frames to land, and
      // `pick` answers at the drawn rung, so an anchor a rung ahead of the
      // geometry is an anchor no click can match.
      anchor = view.anchor && primary.builtRes >= 0
        ? h3.latLngToCell(view.anchor.lat, wrap180HalfOpen(view.anchor.lon), primary.builtRes) : null;
      if (view.anchor && anchor !== null) {
        const centre = h3.cellToLatLng(anchor);
        // On the same ground the grid is draped onto, not on the datum: a
        // marker sunk into relief draws at a pixel showing ground far beyond
        // it, and the pick a click there resolves to is that ground. At LOCAL
        // over Hadley that stood the affordance 34 km - three cells - from the
        // cell it names.
        unitFor(centre[0], wrap360(centre[1]), marker.position);
        if (debug.markerDatum) marker.position.multiplyScalar(view.radiusM * LIFT);
        else drapedPoint(marker.position, view, debug.float, primary.biasM);
        // Constant apparent size: the ring this replaced sized its markers off
        // the radius and each one swelled to fill the screen on the way down.
        const mpp = (2 * Math.tan(THREE.MathUtils.degToRad(view.fovDeg) / 2)
          * Math.max(1, view.altitudeM)) / Math.max(1, view.viewportH);
        marker.scale.setScalar(Math.max(1, mpp * 4));
        marker.visible = pxAcross < 120;
        // The marker's own PLACEMENT against the sun, not its mesh's local
        // normal - see the comment where `markerMat` is built.
        _markerDir.copy(marker.position).normalize();
        const markerLit = debug.unlit
          ? 1 : Math.max(_markerDir.dot(view.sunDirFixed), NIGHT_FLOOR);
        markerMat.color.copy(markerColor).multiplyScalar(markerLit);
      } else {
        marker.visible = false;
      }
      syncHover(view);
    },

    resolutionAt(view: HexOverlayView): number | null {
      if (!h3 || view.baseRes === null) return null;
      const excluded = !debug.forceShape
        && shapeDepartureFrac(view.radiusM, view.ellipsoidAxesM) > SHAPE_DEPARTURE_LIMIT;
      const base = debug.suppress || excluded ? null
        : THREE.MathUtils.clamp(view.baseRes + debug.resStep, 0, 15);
      if (base === null) return null;
      return drawnResolution(h3, base, view.radiusM, view.altitudeM, view.viewportH, view.fovDeg);
    },

    pick(latDeg: number, lonDeg: number): string | null {
      if (!h3 || primary.builtRes < 0) return null;
      return h3.latLngToCell(latDeg, wrap180HalfOpen(lonDeg), primary.builtRes);
    },

    setHoverCell(cell: string | null): void {
      if (hoverCell === cell) return;
      hoverCell = cell;
      if (cell === null) hoverMesh.visible = false;
    },

    anchorCell(): string | null {
      return anchor;
    },

    cellCentre(cell: string): { lat: number; lon: number } | null {
      if (!h3) return null;
      try {
        const [lat, lon] = h3.cellToLatLng(cell);
        return { lat, lon: wrap360(lon) };
      } catch {
        return null;
      }
    },

    covers(latDeg: number, lonDeg: number): boolean {
      if (!h3 || primary.builtRes < 0) return false;
      return primary.cellSet.has(h3.latLngToCell(latDeg, wrap180HalfOpen(lonDeg), primary.builtRes));
    },

    cellSize(): { acrossFlatsM: number; areaKm2: number } | null {
      if (primary.builtRes < 0 || primary.acrossFlatsM === 0) return null;
      return {
        acrossFlatsM: primary.acrossFlatsM,
        areaKm2: HEX_AREA_OVER_W2 * (primary.acrossFlatsM / 1000) ** 2,
      };
    },

    debug(): HexOverlayDebug {
      return {
        ready: h3 !== null,
        baseRes: liveBase,
        drawnRes: primary.builtRes >= 0 ? primary.builtRes : null,
        resStep: debug.resStep,
        suppressed: debug.suppress,
        float: debug.float,
        hardSwap: debug.hardSwap,
        unlit: debug.unlit,
        shapeExcluded: liveExcluded,
        shapeDeparture: liveDeparture,
        shapeLimit: SHAPE_DEPARTURE_LIMIT,
        forceShape: debug.forceShape,
        legacy: debug.legacy,
        datumHorizon: debug.datumHorizon,
        markerDatum: debug.markerDatum,
        hoverCell,
        hoverDrawn: hoverMesh.visible,
        cells: primary.cells,
        edges: primary.edges,
        segments: primary.segments,
        vertices: primary.vertices,
        subdivisions: primary.subdivisions,
        sagCapped: primary.sagCapped,
        stepCapped: primary.stepCapped,
        floatPts: primary.floatPts,
        sagToleranceM: primary.tolM,
        pentagons: primary.pentagons,
        biasM: primary.biasM,
        capHalfAngleDeg: primary.capDeg,
        latDeg: primary.builtLat,
        lonDeg: primary.builtLon,
        camRadiusM: liveCamRadiusM,
        camLatDeg: liveCamLatDeg,
        camLonDeg: liveCamLonDeg,
        horizonRadiusM: liveHorizonRadiusM,
        heightVersion: liveHeightVersion,
        drapedHeightVersion: primary.builtRes >= 0 ? primary.builtHeightVersion : -1,
        building: primary.job !== null,
        altitudeM: liveAlt,
        pxAcross,
        acrossFlatsM: primary.acrossFlatsM,
        anchorCell: anchor,
        fade: secondary && secondary.builtRes >= 0 ? {
          res: secondary.builtRes,
          weight: secondary.material.opacity / 0.85,
          cells: secondary.cells,
          edges: secondary.edges,
          vertices: secondary.vertices,
          subdivisions: secondary.subdivisions,
          segments: secondary.segments,
          pentagons: secondary.pentagons,
        } : null,
      };
    },

    setDebug(next: {
      resStep?: number | null; suppress?: boolean; float?: boolean; hardSwap?: boolean;
      unlit?: boolean; forceShape?: boolean; capScale?: number | null; sinkM?: number | null;
      legacy?: boolean; datumHorizon?: boolean; markerDatum?: boolean;
    }): void {
      if (next.resStep !== undefined) debug.resStep = next.resStep ?? 0;
      if (next.suppress !== undefined) debug.suppress = next.suppress;
      if (next.float !== undefined) debug.float = next.float;
      if (next.hardSwap !== undefined) debug.hardSwap = next.hardSwap;
      if (next.forceShape !== undefined) debug.forceShape = next.forceShape;
      if (next.capScale !== undefined) debug.capScale = next.capScale ?? 1;
      if (next.sinkM !== undefined) debug.sinkM = next.sinkM ?? 0;
      if (next.legacy !== undefined) debug.legacy = next.legacy;
      if (next.datumHorizon !== undefined) debug.datumHorizon = next.datumHorizon;
      if (next.markerDatum !== undefined) debug.markerDatum = next.markerDatum;
      if (next.unlit !== undefined) { debug.unlit = next.unlit; hexUnlitU.value = next.unlit ? 1 : 0; }
      primary.builtRes = -1;
      primary.job = null;
      disposeSecondary();
    },

    dispose(): void {
      disposed = true;
      clearLayer(primary);
      primary.material.dispose();
      disposeSecondary();
      object.remove(marker);
      markerGeo.dispose();
      markerMat.dispose();
      object.remove(hoverMesh);
      hoverMesh.geometry.dispose();
      hoverMat.dispose();
      h3 = null;
    },
  };
}

function edgeKey(p0: number[], p1: number[]): string {
  const k0 = `${p0[0]!.toFixed(7)},${p0[1]!.toFixed(7)}`;
  const k1 = `${p1[0]!.toFixed(7)},${p1[1]!.toFixed(7)}`;
  return k0 < k1 ? `${k0}|${k1}` : `${k1}|${k0}`;
}

function slerp(
  a: THREE.Vector3, b: THREE.Vector3, t: number, out: THREE.Vector3,
): THREE.Vector3 {
  const dot = THREE.MathUtils.clamp(a.dot(b), -1, 1);
  const omega = Math.acos(dot);
  if (omega < 1e-9) return out.copy(a);
  const s = Math.sin(omega);
  return out.copy(a).multiplyScalar(Math.sin((1 - t) * omega) / s)
    .addScaledVector(b, Math.sin(t * omega) / s)
    .normalize();
}

function greatCircleRad(
  lat0: number, lon0: number, lat1: number, lon1: number,
): number {
  if (!Number.isFinite(lat0)) return Infinity;
  const a = THREE.MathUtils.degToRad(lat0);
  const b = THREE.MathUtils.degToRad(lat1);
  const d = THREE.MathUtils.degToRad(lon1 - lon0);
  return Math.acos(THREE.MathUtils.clamp(
    Math.sin(a) * Math.sin(b) + Math.cos(a) * Math.cos(b) * Math.cos(d), -1, 1));
}
